import { useEffect, useState, useRef } from "react";
import { Sparkle, FilePdf, CheckCircle, Clock, Lightning } from "@phosphor-icons/react";

/**
 * Cuenta atrás visual durante la subida + extracción de PDFs.
 *
 * Estimación: 3s overhead de subida + 7s/PDF de extracción Gemini (rough).
 * Mientras hace `await` al backend, el reloj corre. Cuando el backend
 * responde, completamos la barra al 100% y mostramos el resultado.
 */
export default function UploadCountdown({ fileCount, totalSizeBytes, onCancel }) {
  // Estimación heurística (en segundos)
  const uploadSec = Math.max(2, Math.round((totalSizeBytes / (1024 * 1024)) * 0.6)); // ~0.6s por MB
  const extractSec = fileCount * 8; // ~8s por PDF Gemini
  const totalSec = uploadSec + extractSec;

  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const tick = setInterval(() => {
      const e = (Date.now() - startRef.current) / 1000;
      setElapsed(e);
    }, 100);
    return () => clearInterval(tick);
  }, []);

  // No bajamos del 0 ni superamos el 95% mientras seguimos esperando
  const pct = Math.min(95, (elapsed / totalSec) * 100);
  const remaining = Math.max(0, Math.ceil(totalSec - elapsed));

  // Fase actual
  const phase = elapsed < uploadSec ? "upload" : "extract";
  const phaseLabel = phase === "upload"
    ? "Subiendo archivos al servidor"
    : "La IA está leyendo tus PDFs";

  return (
    <div
      data-testid="upload-countdown"
      className="fixed inset-0 z-50 bg-navy/80 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-amber-200">
        {/* Cabecera con gradiente */}
        <div className="relative px-7 pt-7 pb-5 bg-gradient-to-br from-navy to-navy-soft text-white overflow-hidden">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              background:
                "radial-gradient(circle at 20% 30%, #F59E0B 0%, transparent 40%), radial-gradient(circle at 80% 70%, #F59E0B 0%, transparent 40%)",
            }}
          />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="relative inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-400/20">
                <Lightning className="w-4 h-4 text-amber-300 animate-pulse" weight="fill" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-amber-300">
                Procesando
              </span>
            </div>
            <h2 className="font-display font-bold text-2xl tracking-tight">
              {fileCount} siniestro{fileCount !== 1 ? "s" : ""} en marcha
            </h2>
            <p className="text-[13px] text-white/70 mt-1">{phaseLabel}…</p>
          </div>
        </div>

        {/* Cuenta atrás gigante */}
        <div className="px-7 py-6 border-b border-slate-100">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">
                Tiempo restante estimado
              </div>
              <div className="flex items-baseline gap-2">
                <span
                  data-testid="countdown-seconds"
                  className="font-display font-bold text-5xl tracking-tight text-navy tabular-nums"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {remaining}
                </span>
                <span className="text-xl text-muted font-semibold">s</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">
                Progreso
              </div>
              <div className="font-display font-bold text-3xl text-amber-500 tabular-nums">
                {Math.round(pct)}%
              </div>
            </div>
          </div>

          {/* Barra animada */}
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden relative">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 transition-all duration-300 relative overflow-hidden"
              style={{ width: `${pct}%` }}
            >
              <div
                className="absolute inset-0 opacity-40"
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent)",
                  animation: "shimmer 1.6s linear infinite",
                }}
              />
            </div>
          </div>

          <style>{`
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}</style>
        </div>

        {/* Pasos */}
        <div className="px-7 py-5 space-y-2.5">
          <Step
            active={phase === "upload"}
            done={phase !== "upload"}
            icon={FilePdf}
            label="Subiendo PDFs"
            sublabel={`${fileCount} archivo${fileCount !== 1 ? "s" : ""} · ${(totalSizeBytes / 1024 / 1024).toFixed(1)} MB`}
          />
          <Step
            active={phase === "extract"}
            done={false}
            icon={Sparkle}
            label="IA extrayendo datos"
            sublabel="Gemini 3 Flash · siniestro, póliza, riesgo, asegurado…"
          />
          <Step
            active={false}
            done={false}
            icon={CheckCircle}
            label="Catastro y mapa"
            sublabel="Búsqueda automática + dirección normalizada"
          />
        </div>

        {/* Footer */}
        <div className="px-7 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="text-[11px] text-muted inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" weight="bold" />
            <span>~{totalSec}s estimados · {Math.round(extractSec / fileCount)}s por siniestro</span>
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              data-testid="countdown-cancel"
              className="text-[11px] text-muted hover:text-navy underline underline-offset-2"
            >
              Continuar en segundo plano
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ active, done, icon: Icon, label, sublabel }) {
  const stateColor = done
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : active
    ? "bg-amber-100 text-amber-700 border-amber-300 ring-4 ring-amber-100/50"
    : "bg-slate-100 text-slate-400 border-slate-200";

  return (
    <div className="flex items-center gap-3" data-testid={`step-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div
        className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all ${stateColor}`}
      >
        {done ? (
          <CheckCircle className="w-5 h-5" weight="fill" />
        ) : (
          <Icon className={`w-4.5 h-4.5 ${active ? "animate-pulse" : ""}`} weight={active ? "fill" : "bold"} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-[13px] ${active ? "text-navy" : done ? "text-emerald-700" : "text-slate-400"}`}>
          {label}
        </div>
        <div className="text-[11px] text-muted truncate">{sublabel}</div>
      </div>
    </div>
  );
}
